import React, { useEffect, useRef, useState } from "react";
import { Search, LoaderCircle, ChevronLeft, X, Boxes, Bookmark } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, FreeMode } from "swiper/modules";
import { useNavigate } from "react-router-dom";
import "swiper/css";
import "swiper/css/navigation";
import "./SearchSuggestions.css";

import { getSearchSuggestions } from "../../../services/searchService";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

const iconMap = {
    brand: Bookmark,
    group: Boxes,
};

export default function SearchSuggestions({
    value,
    onChange,
    onSubmit,
    placeholder = "جستجوی محصول، برند و دسته‌بندی...",
}) {
    const wrapperRef = useRef(null);
    const requestIdRef = useRef(0);
    const routerNavigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const [results, setResults] = useState({
        products: [],
        landingPage: [],
        relatedContent: [],
    });



    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, []);

    useEffect(() => {
        const query = value.trim();

        if (query.length < MIN_QUERY_LENGTH) {
            setLoading(false);
            setOpen(false);
            setResults({ products: [], landingPage: [], relatedContent: [] });
            return undefined;
        }

        const requestId = ++requestIdRef.current;
        const controller = new AbortController();

        const timer = window.setTimeout(async () => {
            try {
                setLoading(true);
                setOpen(true);

                const data = await getSearchSuggestions(query, controller.signal);

                if (requestId !== requestIdRef.current) return;
                setResults(data);
            } catch (error) {
                if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
                    return;
                }

                console.error("Search suggestions error:", error);

                if (requestId === requestIdRef.current) {
                    setResults({
                        products: [],
                        landingPage: [],
                        relatedContent: [],
                    });
                }
            } finally {
                if (requestId === requestIdRef.current) {
                    setLoading(false);
                }
            }
        }, DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [value]);

    const hasResults =
        results.products.length > 0 ||
        results.landingPage.length > 0 ||
        results.relatedContent.length > 0;

    const handleSubmit = (event) => {
        event.preventDefault();

        const query = value.trim();
        if (!query) return;

        setOpen(false);

        if (onSubmit) {
            onSubmit(query);
            return;
        }

        window.location.href = `/?search=${encodeURIComponent(query)}`;
    };

const navigate = (url) => {
    setOpen(false);

    if (!url) return;

    const normalizedUrl =
        url.startsWith("http://") || url.startsWith("https://")
            ? url
            : `/${url.replace(/^\/+/, "")}`;

    if (normalizedUrl.startsWith("http://") || normalizedUrl.startsWith("https://")) {
        window.location.href = normalizedUrl;
        return;
    }

    routerNavigate(normalizedUrl);
};

    return (
        <div ref={wrapperRef} className={`search-suggestions ${open ? "search-suggestions--open" : ""}`}>
            <form className="search-suggestions__form" onSubmit={handleSubmit}>
                <Search className="search-suggestions__leading-icon" size={18} />

                <input
                    type="search"
                    value={value}
                    onChange={(event) => {
                        onChange(event.target.value);
                        if (event.target.value.trim().length >= MIN_QUERY_LENGTH) {
                            setOpen(true);
                        }
                    }}
                    onFocus={() => {
                        if (value.trim().length >= MIN_QUERY_LENGTH) {
                            setOpen(true);
                        }
                    }}
                    placeholder={placeholder}
                    aria-label="جستجو"
                    autoComplete="off"
                />

                {value && !loading && (
                    <button
                        type="button"
                        className="search-suggestions__clear"
                        aria-label="پاک کردن جستجو"
                        onClick={() => {
                            onChange("");
                            setOpen(false);
                        }}
                    >
                        <X size={15} />
                    </button>
                )}

                <button
                    type="submit"
                    className="search-suggestions__submit"
                    aria-label={loading ? "در حال جستجو" : "انجام جستجو"}
                    disabled={loading}
                >
                    {loading ? (
                        <LoaderCircle size={16} className="search-suggestions__spinner" />
                    ) : (
                        <Search size={15} />
                    )}
                </button>
            </form>

            {open && (
                <div className="search-suggestions__panel">
                    {loading && !hasResults ? (
                        <div className="search-suggestions__state">
                            <LoaderCircle size={23} className="search-suggestions__big-spinner" />
                            <span>در حال جستجو...</span>
                        </div>
                    ) : !hasResults ? (
                        <div className="search-suggestions__state">
                            <Search size={25} />
                            <strong>نتیجه‌ای پیدا نشد</strong>
                            <span>عبارت دیگری را جستجو کنید.</span>
                        </div>
                    ) : (
                        <>

                            {results.products.length > 0 && (
                                <section className="search-suggestions__products">
                                    {/* <div className="search-suggestions__heading">
                                        <h3>محصولات</h3>
                                    </div> */}

                                    <Swiper
                                        modules={[Navigation, FreeMode]}
                                        // navigation
                                        freeMode
                                        spaceBetween={20}
                                        slidesPerView={3.15}
                                        breakpoints={{
                                            0: { slidesPerView: 1.45 },
                                            420: { slidesPerView: 2.1 },
                                            640: { slidesPerView: 2.7 },
                                            900: { slidesPerView: 2.30 },
                                        }}
                                        className="search-suggestions__swiper"
                                    >
                                        {results.products.map((product) => (
                                            <SwiperSlide key={product.id}>
                                                <button
                                                    type="button"
                                                    className="search-product"
                                                    onClick={() => navigate(product.productURL)}
                                                >
                                                    <div className="search-product__image-wrap">
                                                        <img
                                                            src={product.image}
                                                            alt={product.faName}
                                                            loading="lazy"
                                                        />
                                                    </div>

                                                    <div className="search-product__content">
                                                        <strong>{product.faName}</strong>
                                                        {product.brandEnName && (
                                                            <span>{product.brandEnName}</span>
                                                        )}
                                                    </div>
                                                </button>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </section>
                            )}


                            {results.landingPage.length > 0 && (
                                <section className="search-suggestions__landing">
                                    {/* <div className="search-suggestions__heading">
                                        <h3>پیشنهادهای مرتبط</h3>
                                    </div> */}

                                    <div className="search-suggestions__landing-list">
                                        {results.landingPage.map((item) => {
                                            const Icon = iconMap[item.type] || Search;

                                            return (
                                                <button
                                                    type="button"
                                                    key={item.id}
                                                    className="search-suggestions__landing-item"
                                                    onClick={() => navigate(item.url)}
                                                >
                                                    <span className="search-suggestions__landing-icon">
                                                        <Icon size={24} stroke={"#832865"} stroke-width={1} />
                                                    </span>

                                                    <span className="search-suggestions__landing-title">
                                                        {item.title}
                                                    </span>

                                                    <ChevronLeft size={24} />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </section>
                            )}


                            {results.relatedContent.length > 0 && (
                                <section className="search-suggestions__related">
                                    <div className="search-suggestions__heading">
                                        <h3>محتوای مرتبط</h3>
                                    </div>

                                    <div className="search-suggestions__related-list">
                                        {results.relatedContent.map((item) => (
                                            <button
                                                key={item.id}
                                                type="button"
                                                onClick={() => navigate(item.url)}
                                            >
                                                {item.title}
                                            </button>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </>
                    )}

                    {/* <button
                        type="button"
                        className="search-suggestions__view-all"
                        onClick={() => {
                            const query = value.trim();
                            if (!query) return;
                            setOpen(false);
                            window.location.href =
                                `/products?search=${encodeURIComponent(query)}`;
                        }}
                    >
                        مشاهده تمام نتایج برای «{value.trim()}»
                        <ChevronLeft size={16} />
                    </button> */}
                </div>
            )}
        </div>
    );
}
